<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="card my-4">
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
            <div
              class="bg-gradient-success shadow-success border-radius-lg pt-4 pb-3"
            >
              <h6 class="text-white text-capitalize ps-3">
                {{ isEdit ? "編輯活動" : "新增活動" }}
              </h6>
            </div>
          </div>
          <div class="card-body">
            <form @submit.prevent="submitForm">
              <!-- Basic Info -->
              <h6
                class="mb-3 text-uppercase text-secondary text-xs font-weight-bolder"
              >
                基本資訊
              </h6>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">活動名稱</label>
                  <input
                    type="text"
                    class="form-control border p-2"
                    v-model="form.title"
                    required
                  />
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">活動類型</label>
                  <select
                    class="form-select border p-2"
                    v-model="form.type"
                    :disabled="isEdit"
                  >
                    <option value="buy_x_get_y">
                      買商品送商品 (Buy X Get Y)
                    </option>
                    <option value="spend_x_get_y">
                      滿額贈禮 (Spend X Get Gift)
                    </option>
                    <option value="spend_x_off">
                      滿額折扣 (Spend X Get Discount)
                    </option>
                    <option value="buy_n_off">
                      任選優惠 (Buy N Get Discount)
                    </option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">代碼 (可選, 通常不用填)</label>
                  <input
                    type="text"
                    class="form-control border p-2"
                    v-model="form.code"
                  />
                </div>
                <div class="col-md-6 mb-3">
                  <div class="form-check form-switch ps-0 mt-4">
                    <input
                      class="form-check-input ms-auto"
                      type="checkbox"
                      id="isActive"
                      v-model="form.is_active"
                    />
                    <label
                      class="form-check-label text-body ms-3 text-truncate w-80 mb-0"
                      for="isActive"
                      >啟用狀態</label
                    >
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">開始時間</label>
                  <input
                    type="datetime-local"
                    class="form-control border p-2"
                    v-model="form.starts_at"
                  />
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">結束時間</label>
                  <input
                    type="datetime-local"
                    class="form-control border p-2"
                    v-model="form.ends_at"
                  />
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">優先級 (數字越大越優先)</label>
                  <input
                    type="number"
                    class="form-control border p-2"
                    v-model="form.priority"
                  />
                </div>
                <div class="col-md-6 mb-3">
                  <div class="form-check form-switch ps-0 mt-4">
                    <input
                      class="form-check-input ms-auto"
                      type="checkbox"
                      id="stopRules"
                      v-model="form.stop_further_rules"
                    />
                    <label
                      class="form-check-label text-body ms-3 text-truncate w-80 mb-0"
                      for="stopRules"
                      >阻斷後續規則 (排他)</label
                    >
                  </div>
                </div>
              </div>

              <hr class="my-4" />

              <!-- Conditions -->
              <h6
                class="mb-3 text-uppercase text-secondary text-xs font-weight-bolder"
              >
                觸發條件 (Conditions)
              </h6>
              <div class="row">
                <!-- Min Spend -->
                <div
                  class="col-md-6 mb-3"
                  v-if="['spend_x_get_y', 'spend_x_off'].includes(form.type)"
                >
                  <label class="form-label">最低消費金額</label>
                  <input
                    type="number"
                    class="form-control border p-2"
                    v-model="form.conditions.min_spend"
                  />
                </div>

                <!-- Min Quantity -->
                <div
                  class="col-md-6 mb-3"
                  v-if="['buy_x_get_y', 'buy_n_off'].includes(form.type)"
                >
                  <label class="form-label">最低購買數量 (例如: 買3件)</label>
                  <input
                    type="number"
                    class="form-control border p-2"
                    v-model="form.conditions.min_quantity"
                  />
                </div>

                <!-- Product Selection (Buy X or Buy N eligible items) -->
                <div
                  class="col-12 mb-3"
                  v-if="['buy_x_get_y', 'buy_n_off'].includes(form.type)"
                >
                  <label class="form-label">指定商品 (留空代表全館商品)</label>
                  <div class="d-flex flex-wrap gap-2 mb-2">
                    <span
                      v-for="pid in form.conditions.product_ids"
                      :key="pid"
                      class="badge bg-gradient-info d-flex align-items-center"
                    >
                      ID: {{ pid }}
                      <i
                        class="material-icons ms-2 cursor-pointer text-xs"
                        @click="removeProduct('conditions', pid)"
                        >close</i
                      >
                    </span>
                  </div>
                  <button
                    type="button"
                    class="btn btn-outline-dark btn-sm"
                    @click="openProductModal('conditions')"
                  >
                    選擇商品
                  </button>
                </div>
              </div>

              <hr class="my-4" />

              <!-- Rewards -->
              <h6
                class="mb-3 text-uppercase text-secondary text-xs font-weight-bolder"
              >
                優惠內容 (Rewards)
              </h6>
              <div class="row">
                <!-- Discount Type/Value -->
                <div
                  class="col-md-6 mb-3"
                  v-if="['spend_x_off', 'buy_n_off'].includes(form.type)"
                >
                  <label class="form-label">折扣類型</label>
                  <select
                    class="form-select border p-2"
                    v-model="form.rewards.type"
                  >
                    <option value="percent_off">打折 (Percent Off)</option>
                    <option value="fixed_amount_off">
                      折抵金額 (Fixed Amount Off)
                    </option>
                  </select>
                </div>
                <div
                  class="col-md-6 mb-3"
                  v-if="['spend_x_off', 'buy_n_off'].includes(form.type)"
                >
                  <label class="form-label"
                    >數值 ({{
                      form.rewards.type === "percent_off" ? "%" : "$"
                    }})</label
                  >
                  <input
                    type="number"
                    class="form-control border p-2"
                    v-model="form.rewards.value"
                    placeholder="例如: 30 代表 7折 (30% off), 或 100 代表折100元"
                  />
                  <small
                    class="text-muted"
                    v-if="form.rewards.type === 'percent_off'"
                    >輸入 30 代表打7折 (扣除30%)</small
                  >
                </div>

                <!-- Gift Selection -->
                <div
                  class="col-12 mb-3"
                  v-if="['buy_x_get_y', 'spend_x_get_y'].includes(form.type)"
                >
                  <label class="form-label">贈品選擇 (Gift)</label>
                  <div class="mb-2">
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="rewardType"
                        id="rewardTypeGift"
                        value="gift"
                        checked
                        disabled
                      />
                      <label class="form-check-label" for="rewardTypeGift"
                        >贈送商品</label
                      >
                    </div>
                  </div>
                  <div class="d-flex flex-wrap gap-2 mb-2">
                    <span
                      v-for="pid in form.rewards.product_ids"
                      :key="pid"
                      class="badge bg-gradient-danger d-flex align-items-center"
                    >
                      ID: {{ pid }}
                      <i
                        class="material-icons ms-2 cursor-pointer text-xs"
                        @click="removeProduct('rewards', pid)"
                        >close</i
                      >
                    </span>
                  </div>
                  <button
                    type="button"
                    class="btn btn-outline-dark btn-sm"
                    @click="openProductModal('rewards')"
                  >
                    選擇贈品
                  </button>
                </div>

                <div
                  class="col-md-6 mb-3"
                  v-if="['buy_x_get_y', 'spend_x_get_y'].includes(form.type)"
                >
                  <label class="form-label">贈送數量限制</label>
                  <input
                    type="number"
                    class="form-control border p-2"
                    v-model="form.rewards.max_quantity"
                    value="1"
                  />
                </div>
              </div>

              <div class="d-flex justify-content-end mt-4">
                <router-link
                  to="/marketing/promotions"
                  class="btn btn-light me-2"
                  >取消</router-link
                >
                <button type="submit" class="btn btn-success">儲存</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Selection Modal -->
    <div v-if="showProductModal" class="modal-backdrop fade show"></div>
    <div
      class="modal fade"
      :class="{ show: showProductModal }"
      :style="{ display: showProductModal ? 'block' : 'none' }"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">選擇商品</h5>
            <button
              type="button"
              class="btn-close text-dark"
              @click="closeProductModal"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <input
                type="text"
                class="form-control border p-2"
                v-model="productSearch"
                placeholder="搜尋商品名稱..."
                @keyup.enter="searchProducts"
              />
            </div>
            <div class="table-responsive" style="max-height: 400px">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th>選擇</th>
                    <th>圖片</th>
                    <th>名稱</th>
                    <th>價格</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="prod in products" :key="prod.id">
                    <td>
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :value="prod.id"
                          v-model="tempSelectedProducts"
                        />
                      </div>
                    </td>
                    <td>
                      <img
                        :src="prod.image_url || '/placeholder.jpg'"
                        class="avatar avatar-sm me-3"
                      />
                    </td>
                    <td>
                      <h6 class="mb-0 text-sm">{{ prod.name }}</h6>
                      <p class="text-xs text-secondary mb-0">
                        {{ prod.product_code }}
                      </p>
                    </td>
                    <td>NT$ {{ prod.price }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeProductModal"
            >
              關閉
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="confirmProductSelection"
            >
              確認選擇
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/utils/request";
import Swal from "sweetalert2";

export default {
  name: "PromotionForm",
  data() {
    return {
      isEdit: false,
      form: {
        title: "",
        code: "",
        type: "buy_x_get_y",
        starts_at: null,
        ends_at: null,
        is_active: true,
        priority: 0,
        stop_further_rules: false,
        conditions: {
          min_spend: null,
          min_quantity: null,
          product_ids: [],
        },
        rewards: {
          type: "percent_off",
          value: null,
          product_ids: [],
          max_quantity: 1,
        },
      },
      // Product Modal
      showProductModal: false,
      currentTarget: "conditions", // conditions or rewards
      products: [],
      productSearch: "",
      tempSelectedProducts: [],
    };
  },
  async mounted() {
    const id = this.$route.params.id;
    if (id) {
      this.isEdit = true;
      await this.fetchPromotion(id);
    }
  },
  methods: {
    async fetchPromotion(id) {
      try {
        const response = await axios.get(`/api/admin/promotions/${id}`);
        this.form = response.data;
        // Transform dates
        if (this.form.starts_at)
          this.form.starts_at = this.form.starts_at.slice(0, 16);
        if (this.form.ends_at)
          this.form.ends_at = this.form.ends_at.slice(0, 16);

        // Ensure structure defaults
        if (!this.form.conditions) this.form.conditions = { product_ids: [] };
        if (!this.form.rewards) this.form.rewards = { product_ids: [] };
      } catch (error) {
        console.error("Error fetching promotion:", error);
      }
    },
    async submitForm() {
      try {
        const payload = { ...this.form };

        // Cleanup types
        if (
          payload.type === "buy_x_get_y" ||
          payload.type === "spend_x_get_y"
        ) {
          payload.rewards.type = "gift";
        }

        if (this.isEdit) {
          await axios.put(`/api/admin/promotions/${this.form.id}`, payload);
          Swal.fire("成功", "活動已更新", "success");
        } else {
          await axios.post("/api/admin/promotions", payload);
          Swal.fire("成功", "活動已建立", "success");
          this.$router.push("/marketing/promotions");
        }
      } catch (error) {
        console.error(error);
        const msg = error.response?.data?.message || "操作失敗";
        Swal.fire("錯誤", msg, "error");
      }
    },
    // Product Modal Methods
    openProductModal(target) {
      this.currentTarget = target;
      this.tempSelectedProducts = [...(this.form[target].product_ids || [])];
      this.showProductModal = true;
      this.searchProducts();
    },
    closeProductModal() {
      this.showProductModal = false;
    },
    async searchProducts() {
      try {
        const response = await axios.get("/api/admin/products", {
          params: { search: this.productSearch, per_page: 20 }, // Ensure backend supports search
        });
        this.products = response.data.data;
      } catch (error) {
        console.error(error);
      }
    },
    confirmProductSelection() {
      if (this.currentTarget === "conditions") {
        this.form.conditions.product_ids = [...this.tempSelectedProducts];
      } else {
        this.form.rewards.product_ids = [...this.tempSelectedProducts];
      }
      this.closeProductModal();
    },
    removeProduct(target, pid) {
      if (target === "conditions") {
        this.form.conditions.product_ids = this.form.conditions.product_ids.filter(
          (id) => id !== pid
        );
      } else {
        this.form.rewards.product_ids = this.form.rewards.product_ids.filter(
          (id) => id !== pid
        );
      }
    },
  },
};
</script>
